import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LinkingOptions, NavigationContainer, useNavigation } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import Login from './src/app/login/Login';
import Pages from './src/app/pages/Pages';
import { useTheme } from 'react-native-paper';
import { NavigationTheme } from 'react-native-paper/lib/typescript/types';
import { PublicAgenda } from './src/core/models/TherapistModel';
import Therapist from './src/app/Therapist/Therapist';
import { SessionContext } from './src/core/provider/SessionProvider';
import { useTherapists } from './src/core/api/therapists';
import SelectTherapist from './src/app/Therapist/SelectTherapist';
import { AppStoreContext } from './src/core/provider/AppStoreProvider';
import { useMe } from './src/core/api/me';
import LoadingScreen from './src/components/LoadingScreen';
import { useChat } from './src/core/api/chat';
import AppleStatusBarPadding from './src/components/AppleStatusBarPadding';
import RequestUserName from './src/app/Profile/RequestUserName';

export type RootNavParamList = {
    "Login": {
        therapistId?: number;
        clinicId?: number;
    } | undefined,
    // "Onboarding": undefined,
    "Pages": undefined,
    "SelectTherapist": undefined,
    "Therapist": PublicAgenda,
    // "ChatKyra": undefined,
    // "ChatTherapist": undefined,
    "RequestUserName": undefined
};

const linking: LinkingOptions<RootNavParamList> = {
    enabled: true,
    prefixes: [
        /* your linking prefixes */
    ],
};

const Stack = createNativeStackNavigator<RootNavParamList>();

const App: React.FC = () => {

    const session = useContext(SessionContext);
    const appContext = useContext(AppStoreContext);

    const [ready, setReady] = useState(false);
    const [requestedTherapist, setRequestedTherapist] = useState<PublicAgenda>();

    const [therapistId, setTherapistId] = useState<number | undefined>(undefined);
    const [clinicId, setClinicId] = useState<number | undefined>(undefined);

    const hasName = useMemo(() => {
        return appContext.myProfile?.value?.full_name != "";
    }, [appContext]);
    const hasRequestedTherapist = useMemo(() => {
        return requestedTherapist !== undefined;
    }, [requestedTherapist]);
    const hasSession = useMemo(() => {
        return session.session?.current !== undefined;
    }, [session]);
    const hasTherapist = useMemo(() => {
        return appContext.myTherapist?.value !== undefined;
    }, [appContext]);

    const theme = useTheme();
    const therapists = useTherapists();
    const me = useMe();
    const chat = useChat();

    const checkSession = () => {
        if (hasSession) {
            return true;
        }

        if (location.pathname !== "/" && location.pathname !== "/therapist") {
            location.href = "/";
        }

        return;
    }

    const checkTherapistInPath = () => {
        if (location.pathname !== "/therapist") {
            return;
        }
        const params = new URLSearchParams(location.search);
        const clinicId = Number(params.get('clinic'));
        const therapistId = Number(params.get('id'));
        if (!therapistId || isNaN(therapistId) || !clinicId || isNaN(clinicId)) {
            return;
        }

        setTherapistId(therapistId);
        setClinicId(clinicId);

        return { therapistId, clinicId };
    };

    const loadTherapistFromPath = async ({ therapistId, clinicId }: { therapistId?: number, clinicId?: number } = {}) => {
        console.log('Loading therapist from path', therapistId, clinicId);
        if (!therapistId || isNaN(therapistId) || !clinicId || isNaN(clinicId)) {
            return;
        }

        const therapist = await therapists.getTherapist(clinicId, therapistId)
            .catch(() => undefined);
        if (!therapist) {
            return;
        }
        setRequestedTherapist(therapist);
    };

    const init = async () => {
        const therapistParams = checkTherapistInPath();
        console.log('Therapist params from path:', therapistParams);

        if (!checkSession()) {
            setReady(true);
            return;
        }

        await Promise.all([
            loadTherapistFromPath(therapistParams),
            me.fetchMyProfile(),
            therapists.fetchMyTherapist(),
        ]).catch((error) => {
            console.error('Error fetching data', error);

            setTimeout(() => {
                //mmm What could had happened?
                location.reload();
            }, 5000);

            return;
        });

        history.replaceState({}, '', '/');
        setReady(true);
    };

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        //TODO: Enable chat when we have a therapist
        // if (hasTherapist) {
        //     console.log('Connecting chat socket');
        //     chat.connectChatSocket();
        // }
    }, [hasTherapist]);

    return (<>
        {ready ?
            <NavigationContainer
                documentTitle={{
                    //TODO: We should put this in a constants file
                    formatter: (options, route) =>
                        "Kyra Health",
                }}
                linking={linking}
                theme={theme as unknown as NavigationTheme}>
                <Stack.Navigator id='AppNavigator'
                    screenOptions={
                        { headerShown: false }
                    }
                    initialRouteName={hasSession ?
                        (!hasName ? 'RequestUserName' :
                            (hasRequestedTherapist ? 'Therapist' :
                                (hasTherapist ? 'Pages' : 'SelectTherapist')))
                        : 'Login'}
                >

                    {!hasSession && <Stack.Screen
                        name="Login"
                        component={Login}
                        initialParams={{
                            therapistId: therapistId,
                            clinicId: clinicId
                        }}
                    />}

                    {hasSession && <Stack.Screen
                        name="Pages"
                        component={Pages}
                    />}

                    {hasSession && <Stack.Screen
                        name="SelectTherapist"
                        component={SelectTherapist}
                        listeners={
                            {
                                focus: (e) => {
                                    if (hasTherapist) {
                                        //TODO: We have to check how to make this using navigation...
                                        location.replace("/");
                                    }
                                }
                            }
                        }
                    />}

                    {hasSession && <Stack.Screen
                        name="Therapist"
                        component={Therapist}
                        initialParams={requestedTherapist}
                    />}

                    <Stack.Screen
                        name="RequestUserName"
                        component={RequestUserName}
                    />

                    {/* {hasSession && <Stack.Screen
                        name="ChatKyra"
                        component={ChatKyra} />} */}

                    {/* {hasSession && <Stack.Screen
                        name="ChatTherapist"
                        component={ChatTherapist} />} */}
                </Stack.Navigator>
                <AppleStatusBarPadding
                    bgColor={theme.colors.surface}
                    height={20} />
            </NavigationContainer > :
            <LoadingScreen />
        }
    </>);
}

export default App;
